// Per-run game state container. Framework-free: it owns the seeded RNG, the
// current marshmallow's heat state, score/combo/progress, and drives one step
// of simulation at a time. Phaser scenes call applyStep() each frame and read
// the returned event to drive presentation.

import { makeRng, deriveSeed, type Rng } from './rng';
import { difficultyFor, type Difficulty } from './difficulty';
import {
  freshHeatState,
  stepHeat,
  bandCenteredness,
  type HeatState,
  type HeatParams,
} from './heat-model';
import { makeWind, type Wind } from './wind';
import { scoreMarshmallow } from './scoring';
import { COOLING_PER_SEC, TOAST_RATE_PER_SEC, BURN_THRESHOLD, TOAST_TARGET, BURN_LIMIT } from './constants';

/** What happened during a single applyStep() call. */
export type StepEvent = 'none' | 'toasted' | 'burnt' | 'gameover';

export interface StepResult {
  event: StepEvent;
  /** Points gained this step (only non-zero on 'toasted'). */
  gained: number;
}

export class RunState {
  readonly rng: Rng;
  heat: HeatState;
  score = 0;
  combo = 0;
  marshmallowsDone = 0;
  burnsRemaining: number;

  private difficulty: Difficulty;
  private params: HeatParams;
  private wind: Wind;
  private windSeed: number;
  private elapsed = 0; // seconds on the current marshmallow
  private inBandTime = 0; // seconds spent in band this marshmallow
  private qualityAccum = 0; // ∫ centeredness dt while in band
  private gameOver = false;

  constructor(seed: number, burnLimit: number = BURN_LIMIT) {
    this.rng = makeRng(seed);
    this.burnsRemaining = burnLimit;
    this.windSeed = seed;
    // Initialized concretely in startMarshmallow(); assigned here to satisfy
    // strict-mode definite-assignment without a non-null assertion.
    this.difficulty = difficultyFor(0);
    this.params = this.paramsFor(this.difficulty);
    this.wind = makeWind(makeRng(this.windSeed), this.difficulty.windAmp);
    this.heat = freshHeatState();
    this.startMarshmallow();
  }

  private paramsFor(d: Difficulty): HeatParams {
    return {
      flameIntensity: d.flameIntensity,
      cooling: COOLING_PER_SEC,
      bandMin: d.bandMin,
      bandMax: d.bandMax,
      toastRate: TOAST_RATE_PER_SEC,
      burnThreshold: BURN_THRESHOLD,
    };
  }

  /** Begin a fresh marshmallow at the current difficulty. */
  startMarshmallow(): void {
    this.difficulty = difficultyFor(this.marshmallowsDone);
    this.params = this.paramsFor(this.difficulty);
    // Derive a per-marshmallow wind stream so gusts differ run-to-run but stay
    // deterministic from the run seed.
    this.windSeed = deriveSeed(this.windSeed, `m${this.marshmallowsDone}`);
    this.wind = makeWind(makeRng(this.windSeed), this.difficulty.windAmp);
    this.heat = freshHeatState();
    // INTENTIONAL: elapsed resets to 0 each marshmallow, so wind.at(elapsed)
    // restarts from a calm phase on every fresh marshmallow — a fair, readable
    // start. Rising run-long pressure comes from difficulty.windAmp growing
    // with marshmallowsDone, not from a continuous global wind clock.
    this.elapsed = 0;
    this.inBandTime = 0;
    this.qualityAccum = 0;
  }

  /** Current difficulty snapshot (for HUD / rendering). */
  getDifficulty(): Difficulty {
    return this.difficulty;
  }

  /** Seconds spent on the current marshmallow. */
  getElapsed(): number {
    return this.elapsed;
  }

  /**
   * Advance the simulation by `dt` seconds with the player holding the given
   * normalized distance (0 = at flame, 1 = far). Returns the event that
   * occurred so the caller can react (sfx, scene change, etc.).
   */
  applyStep(distanceNorm: number, dt: number): StepResult {
    if (this.gameOver) return { event: 'gameover', gained: 0 };

    const windHeat = this.wind.at(this.elapsed);
    this.heat = stepHeat(this.heat, distanceNorm, dt, this.params, windHeat);
    this.elapsed += dt;

    if (this.heat.inBand) {
      this.inBandTime += dt;
      this.qualityAccum += bandCenteredness(this.heat.heat, this.params.bandMin, this.params.bandMax) * dt;
    }

    if (this.heat.burnt) {
      return this.onBurn();
    }

    if (this.heat.toastedness >= TOAST_TARGET) {
      return this.onToasted();
    }

    return { event: 'none', gained: 0 };
  }

  private onToasted(): StepResult {
    const quality = this.inBandTime > 0 ? this.qualityAccum / this.inBandTime : 0;
    const gained = scoreMarshmallow({ quality, timeSeconds: this.elapsed, combo: this.combo });
    this.score += gained;
    this.combo += 1;
    this.marshmallowsDone += 1;
    this.startMarshmallow();
    return { event: 'toasted', gained };
  }

  /** Burn the current marshmallow: reset combo, spend a burn, maybe end run.
   *  Private — driven only from applyStep() when heat crosses the burn
   *  threshold; tests exercise it by holding at the flame until it fires. */
  private onBurn(): StepResult {
    this.combo = 0;
    this.burnsRemaining -= 1;
    if (this.isGameOver()) {
      this.gameOver = true;
      return { event: 'gameover', gained: 0 };
    }
    this.startMarshmallow();
    return { event: 'burnt', gained: 0 };
  }

  isGameOver(): boolean {
    return this.gameOver || this.burnsRemaining <= 0;
  }
}
